import React from 'react';
import { Link, useNavigate } from 'react-router-dom';
import type { UseQueryResult } from '@tanstack/react-query';
import { ArrowRight, ChevronDown, Wallet } from 'lucide-react';
import { EmptyState } from '@/components/EmptyState';
import { BudgetLineRow, UnassignedStrip } from '@/components/budget';
import { Button } from '@/components/ui/button';
import type { BudgetSummary, Category } from '@/lib/api';
import { ROUTES } from '@/lib/constants';
import { formatCurrency, formatPercentGlance } from '@/lib/format';
import {
  BUDGET_ADJUST_GOALS,
  BUDGET_COLUMN_LABELS,
  BUDGET_FIRST_RUN,
  BUDGET_INCOME_LABEL,
  BUDGET_LINES_HEADING,
  BUDGET_LOAD_ERROR_TITLE,
  BUDGET_NO_INCOME_HINT,
  BUDGET_NO_LIMIT,
  BUDGET_READING_HELP,
  BUDGET_READING_HELP_TITLE,
  BUDGET_SCOPE_NOTE,
  BUDGET_SUMMARY_LABEL,
  budgetPastLimit,
} from '@/lib/translations';
import { assignmentState, countPastLimit } from '@/lib/utils/budget';
import { Figure, FigureStrip } from './Figure';
import { ReadingError } from './ReadingError';
import { ReadingSkeleton } from './ReadingSkeleton';

// Under Utilizado: "todos os grupos dentro do limite", or the count of groups past it with the expense dot —
// the same anchor the rows below carry.
const PastLimitReference = ({ count }: { count: number }) =>
  count > 0 ? (
    <span className="font-semibold text-foreground">
      <span className="mr-1.5 inline-block h-1.5 w-1.5 rounded-full bg-expense align-middle" aria-hidden="true" />
      {budgetPastLimit(count)}
    </span>
  ) : (
    <>{budgetPastLimit(count)}</>
  );

// The words a row can carry, explained once at the foot of the section — closed by default, a native <details>
// so it needs no state and works without script (no modal, no tooltip to hunt for).
const ReadingHelp = () => (
  <details className="group text-sm">
    <summary className="inline-flex min-h-11 cursor-pointer list-none items-center gap-1 rounded-sm font-medium text-muted-foreground hover:text-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 focus-visible:ring-offset-background sm:min-h-9 [&::-webkit-details-marker]:hidden">
      {BUDGET_READING_HELP_TITLE}
      <ChevronDown
        className="h-3.5 w-3.5 transition-transform group-open:rotate-180 motion-reduce:transition-none"
        aria-hidden="true"
      />
    </summary>
    <dl className="mt-2 max-w-xl space-y-1.5 pb-1 text-muted-foreground">
      {BUDGET_READING_HELP.map(({ term, meaning }) => (
        <div key={term}>
          <dt className="inline font-semibold text-foreground">{term}</dt>
          <dd className="inline"> — {meaning}</dd>
        </div>
      ))}
    </dl>
  </details>
);

interface BudgetOverviewProps {
  summary: BudgetSummary;
  /** The month read, as "2026-09" — carried into each row's way to Categorias. */
  period?: string;
  /** Unclassified categories; the summary can't say which categories hold the unassigned money. */
  unassignedCategories: number;
}

// Every total is read from the summary, never re-derived: they round once on the server, so summing the
// rounded lines here would drift by a cent, and total_must_spend only equals income while the goals sum
// to 100 and nothing is earmarked (TRD §8.7).
const BudgetOverview: React.FC<BudgetOverviewProps> = ({ summary, period, unassignedCategories }) => {
  const hasIncome = parseFloat(summary.income) > 0;
  const totalMustSpend = parseFloat(summary.total_must_spend);
  // overall_used follows the bucket rule (§9): with no limit the API's 0.00 is a convention, not a measurement.
  const hasLimit = totalMustSpend > 0;
  const hasUnassigned = parseFloat(summary.unassigned) !== 0;

  return (
    <div className="animate-fade-in space-y-8">
      <p className="max-w-2xl text-sm text-muted-foreground">{BUDGET_SCOPE_NOTE}</p>

      <FigureStrip
        label={BUDGET_SUMMARY_LABEL}
        // The figures stay when there is no income — `spent` is still real — so a hint, not an empty state.
        note={!hasIncome && <p className="mt-5 max-w-xl text-sm text-muted-foreground">{BUDGET_NO_INCOME_HINT}</p>}
      >
        <Figure label={BUDGET_INCOME_LABEL} anchor="income" value={formatCurrency(summary.income)} />
        <Figure label={BUDGET_COLUMN_LABELS.spent} anchor="expense" value={formatCurrency(summary.total_spent)} />
        <Figure
          className="col-span-2 sm:col-span-1"
          label={BUDGET_COLUMN_LABELS.used}
          value={hasLimit ? formatPercentGlance(summary.overall_used) : '—'}
          // The average hides the exception, so the reference names it: how many groups went past their limit.
          reference={hasLimit ? <PastLimitReference count={countPastLimit(summary.lines)} /> : BUDGET_NO_LIMIT}
        />
      </FigureStrip>

      {hasUnassigned && <UnassignedStrip amount={summary.unassigned} categoryCount={unassignedCategories} />}

      {/* Named, not headed: each row's own h3 sits directly under the section's h2. */}
      <section aria-label={BUDGET_LINES_HEADING}>
        <ul className="divide-y divide-border/50">
          {summary.lines.map((line) => (
            <BudgetLineRow key={line.type} line={line} period={period} />
          ))}
        </ul>
        {/* Each row already says its goal ("meta 40%"), so the footer is how to read the rows and how to change them. */}
        <div className="flex flex-wrap items-start justify-between gap-x-6 gap-y-2 border-t border-border/60 pt-3 text-sm">
          <ReadingHelp />
          <Button
            variant="ghost"
            size="sm"
            asChild
            className="-mr-3 h-11 text-muted-foreground hover:bg-muted hover:text-foreground sm:h-9"
          >
            <Link to={ROUTES.goals}>
              {BUDGET_ADJUST_GOALS}
              <ArrowRight className="h-3.5 w-3.5" />
            </Link>
          </Button>
        </div>
      </section>
    </div>
  );
};

export interface BudgetReadingProps {
  summaryQuery: UseQueryResult<BudgetSummary>;
  /** The unfiltered category list: it decides the first-run state and counts the unclassified ones. */
  categoriesQuery: UseQueryResult<Category[]>;
  /** The month read, as "2026-09". */
  period?: string;
}

// Painel's Orçamento section: am I inside my limits this month? Its own states — loading, first run, failure —
// fill only this panel, so the period bar and the cash flow above stay usable around it.
export const BudgetReading: React.FC<BudgetReadingProps> = ({ summaryQuery, categoriesQuery, period }) => {
  const navigate = useNavigate();
  const { data: summary } = summaryQuery;
  const { data: categories } = categoriesQuery;

  if (categoriesQuery.isPending) return <ReadingSkeleton variant="budget" />;

  // Day one: nothing classified yet, so every allowance reads zero. Six zero rows are noise (§8.6) — the panel
  // points at the work instead. Different from "classified, but no movement this month", which keeps the rows.
  // It needs only the categories, so it doesn't wait for the summary. The action is quiet: Painel's amber is
  // already spent on "Nova Transação".
  if (categories !== undefined && categories.every((c) => assignmentState(c) === 'unassigned')) {
    return (
      <EmptyState
        compact
        icon={Wallet}
        title={BUDGET_FIRST_RUN.title}
        description={BUDGET_FIRST_RUN.description}
        action={{ label: BUDGET_FIRST_RUN.action, onClick: () => navigate(ROUTES.categories), variant: 'secondary' }}
      />
    );
  }

  if (summaryQuery.isPending) return <ReadingSkeleton variant="budget" />;

  if (!summary) {
    return (
      <ReadingError
        title={BUDGET_LOAD_ERROR_TITLE}
        error={summaryQuery.error}
        onRetry={() => void summaryQuery.refetch()}
      />
    );
  }

  const unassignedCategories = categories?.filter((c) => assignmentState(c) === 'unassigned').length ?? 0;

  return <BudgetOverview summary={summary} period={period} unassignedCategories={unassignedCategories} />;
};
